import React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Sidebar } from '@/components/ui-v2/sidebar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Command, CommandInput } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import {
  CategoryTree,
  DealItem,
  DealItemUpdateInput,
  PricingMode,
} from '@/lib/graphql/generated/graphql';
import { useCountryNames } from '@/lib/hooks/useCountryNames';
import { useExecuteOptimisticUpdate } from '@/lib/hooks/useExecuteOptimisticUpdate';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { trackEvent } from '@/lib/mixpanel';
import { cn, getVatLabelKey } from '@/lib/utils';
import { computeProductGroupKey } from '@/lib/utils/product-grouping';
import {
  areAllShippingFeesZero,
  shouldHideShippingPolicyDialog,
} from '@/lib/utils/shipping-policy-utils';
import { dealIdentifierState } from '@/states/deal-identifier.state';
import { filteredRowsCountState } from '@/states/filtered-flat-rows.state';
import { pricingModeState } from '@/states/pricing-mode.state';
import { productsState } from '@/states/products.state';
import {
  selectedProductIdsState,
  selectedProductsCountState,
  useResetSelection,
} from '@/states/row-selection.state';
import { shippingPolicyState } from '@/states/shipping-policy.state';
import { storeAtom } from '@/states/store.state';
import { Currency } from '@/types/currency';
import { ShippingMode } from '@/types/shipping';
import { Store } from '@/types/store';
import { getDefaultStore, useAtomValue } from 'jotai';
import { AlertTriangle, ChevronsUpDown } from 'lucide-react';

import { CategorySelector } from './category-selector';
import { CountryList } from './country-list';
import ShippingPolicyDialog from './ui-v2/shipping-policy/shipping-policy-dialog';
import { Checkbox } from './ui/checkbox';

const confirmDialogFieldLabels: Record<string, string> = {
  chooseDiscount: 'kpi.choose_discount',
  choosePrice: 'kpi.choose_price',
  discountedPrice: 'kpi.discounted_price',
  buyingPrice: 'kpi.buying_price',
  listPrice: 'kpi.list_price',
  marginPercentage: 'kpi.margin_percentage',
  shippingPrice: 'kpi.shipping_price',
  madeIn: 'kpi.made_in',
  stockQuantity: 'kpi.stock',
  hasVatMargin: 'kpi.vat_ms_long',
  productCategoryId: 'kpi.type_long',
  eeePrice: 'kpi.eee_price',
  gtin: 'kpi.gtin',
};

export interface BulkEditSidebarProps {
  onClose: () => void;
}

const BulkEditSidebar: React.FC<BulkEditSidebarProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const dealId = useAtomValue(dealIdentifierState);
  const store = useAtomValue(storeAtom);
  const vatLabel = t(getVatLabelKey(store));
  const user = useUser();
  const products = useAtomValue(productsState);

  const selectedProductIds = useAtomValue(selectedProductIdsState);
  const totalCount = useAtomValue(filteredRowsCountState);

  const { resetSelection } = useResetSelection();
  const { updateProducts } = useUpdateDealItems(dealId);
  const country = useCountryNames();
  const { executeOptimisticUpdate } = useExecuteOptimisticUpdate();

  // Internal state
  const [countryPopoverOpen, setCountryPopoverOpen] = useState(false);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [hasVatMargin, setHasVatMargin] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryTree | null>(
    null
  );
  const [showShippingPolicyModal, setShowShippingPolicyModal] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    field: keyof DealItem | null;
    value: string;
    validationFn?: (value: number) => boolean;
  }>({
    open: false,
    field: null,
    value: '',
  });

  // Computed values
  const isBrand = !user?.isInternalUser;

  const hasFieldLabel =
    !!confirmDialog.field && !!confirmDialogFieldLabels[confirmDialog.field];

  // Compute counts (use derived atoms)
  const selectedCount = useAtomValue(selectedProductsCountState);
  const pricingMode = useAtomValue(pricingModeState);
  const isBuyingPriceFixedMode = pricingMode === PricingMode.BuyingPriceFixed;
  const isMarginFixedMode = pricingMode === PricingMode.MarginFixed;

  // After a successful bulk edit: reset selection then close
  const closeAndResetSelection = () => {
    resetSelection();
    onClose();
  };

  // Handle bulk edit field (moved from product-view)
  const handleBulkEditField = async (
    field: keyof DealItem,
    value: DealItem[keyof DealItem]
  ): Promise<boolean> => {
    const selectedProducts = products.filter((product) =>
      selectedProductIds.includes(product.id)
    );

    if (selectedProducts.length === 0) {
      toast({
        title: t('general.error_no_product_selected'),
        description: t('general.error_please_select_products'),
        variant: 'destructive',
      });
      return false;
    }

    const itemsToSave: DealItemUpdateInput[] = selectedProducts.map(
      (product) => ({
        id: product.id,
        [field]: value,
      })
    );

    const selectedProductsIds = new Set(selectedProducts.map((u) => u.id));
    return executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.map((p) => {
          if (!selectedProductsIds.has(p.id)) return p;
          const nextVersion = { ...p.activeVersion, [field]: value };
          return {
            ...p,
            activeVersion: {
              ...nextVersion,
              productGroupKey: computeProductGroupKey(
                nextVersion.name,
                nextVersion.isSelected
              ),
            },
          };
        }),
      mutation: () => updateProducts(itemsToSave),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_modifications'),
      defaultErrorMessage: t('general.error_cannot_save_modifications'),
      errorContext: 'handleBulkEditField',
      inProgressMessage: t('general.modifying_products_in_progress'),
      onSuccess: () => {
        trackEvent('BULK_EDIT_APPLIED', {
          dealId,
          fieldName: String(field),
          affectedCount: selectedProducts.length,
        });
        const successToast = toast({
          title: t('general.success'),
          description: t('general.success_modified_products'),
          variant: 'default',
        });
        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          successToast.dismiss();
        }, 5000);
      },
      onError: (errorMessage) => {
        trackEvent('BULK_EDIT_FAILED', {
          dealId,
          fieldName: String(field),
          affectedCount: selectedProducts.length,
          errorMessage,
        });
      },
    });
  };

  const openConfirmDialog = (
    field: keyof DealItem,
    value: string,
    validationFn?: (value: number) => boolean
  ) => {
    setConfirmDialog({
      open: true,
      field,
      value,
      validationFn,
    });
  };

  const handleShippingPolicyEdit = () => {
    const store = getDefaultStore();
    const updatedProducts = store.get(productsState);
    const currentShippingPolicy = store.get(shippingPolicyState);
    const allShippingFeesZero = areAllShippingFeesZero(updatedProducts);

    const shippingPolicyWithUpdatedFees = {
      ...currentShippingPolicy,
      allShippingFeesZero,
    };

    const hasShippingPolicy = shouldHideShippingPolicyDialog({
      shippingPolicy: shippingPolicyWithUpdatedFees,
      mode: 'edit',
    });

    if (!hasShippingPolicy) {
      setShowShippingPolicyModal(true);
      return;
    }

    closeAndResetSelection();
    return;
  };

  // Generic handler for all field types. Returns true when the edit succeeded.
  const handleFieldEdit = async (
    field: keyof DealItem,
    value: string,
    validationFn?: (value: number) => boolean
  ): Promise<boolean> => {
    let processedValue: DealItem[keyof DealItem] =
      null as DealItem[keyof DealItem];

    if (value.trim()) {
      processedValue = value.trim();
    }

    if (validationFn && value.trim()) {
      const numericValue = parseFloat(value.replace(',', '.'));
      if (!validationFn(numericValue)) {
        toast({
          title: t('general.error'),
          description: t('general.error_non_authorized_value'),
          variant: 'destructive',
        });
        return false;
      }
      processedValue = numericValue;
    }

    if (field === 'hasVatMargin' && value.trim()) {
      processedValue = (value === 'true') as DealItem[keyof DealItem];
    }

    const success = await handleBulkEditField(
      field as keyof DealItem,
      processedValue
    );
    if (success) {
      setInputs((prev) => ({ ...prev, [field]: '' }));
    }
    return success;
  };

  const handleConfirmedEdit = async () => {
    if (!confirmDialog.field) return;

    const field = confirmDialog.field;
    const success = await handleFieldEdit(
      field,
      confirmDialog.value,
      confirmDialog.validationFn
    );

    setConfirmDialog({ open: false, field: null, value: '' });

    if (field === 'shippingPrice' && success) {
      handleShippingPolicyEdit();
    } else if (success) {
      closeAndResetSelection();
    }
    if ((field as string) === 'productCategoryId' && success) {
      setSelectedCategory(null);
    }
  };

  const updateHasVatMargin = (value: boolean) => {
    setHasVatMargin(value);
  };

  const updateInput = (field: string, value: string) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (category: CategoryTree) => {
    setSelectedCategory(category);
  };
  return (
    <>
      <Sidebar
        title={t('components.bulk_edit_sidebar.title')}
        onClose={onClose}
        zIndexClass="z-bulk-edit-sidebar"
      >
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-medium">
            {t('general.products_selected')} :{' '}
          </span>
          <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
            {selectedCount} / {totalCount}
          </span>
        </div>

        <div className="space-y-8">
          {/* Section Prix */}
          <div className="space-y-4">
            <h4 className="border-b border-gray-200 pb-1 text-sm font-medium text-gray-700">
              {t('components.bulk_edit_sidebar.prices')}
            </h4>

            <div className="ml-2 space-y-4">
              {/* Prix Public TTC*/}
              <div className="space-y-2">
                <Label>
                  {t('kpi.list_price')} ({vatLabel}) (
                  {store === Store.FR ? Currency.EUR : Currency.USD})
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.listPrice || ''}
                    onChange={(e) => updateInput('listPrice', e.target.value)}
                    type="text"
                    placeholder="ex: 24.99"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'listPrice',
                        inputs.listPrice || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* Discount Choose */}
              <div className="space-y-2">
                <Label>{t('kpi.choose_discount')} (%)</Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.chooseDiscount || ''}
                    onChange={(e) =>
                      updateInput('chooseDiscount', e.target.value)
                    }
                    type="text"
                    placeholder="ex: 25"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'chooseDiscount',
                        inputs.chooseDiscount || '',
                        (value) => value >= 0 && value <= 100
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* Prix Choose Final */}
              <div className="space-y-2">
                <Label>
                  {t('kpi.choose_price')} ({vatLabel}) (
                  {store === Store.FR ? Currency.EUR : Currency.USD})
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.choosePrice || ''}
                    onChange={(e) => updateInput('choosePrice', e.target.value)}
                    type="text"
                    placeholder="ex: 24.99"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'choosePrice',
                        inputs.choosePrice || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* Marge Choose */}
              {!isBrand && isMarginFixedMode && (
                <div className="space-y-2">
                  <Label>{t('kpi.margin_percentage')}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={inputs.marginPercentage || ''}
                      onChange={(e) =>
                        updateInput('marginPercentage', e.target.value)
                      }
                      type="text"
                      placeholder="ex: 30"
                      className="flex-1"
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog(
                          'marginPercentage',
                          inputs.marginPercentage || '',
                          (value) => value >= 0
                        )
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Prix de rétrocession*/}
              {isBuyingPriceFixedMode && (
                <div className="space-y-2">
                  <Label>
                    {t('kpi.buying_price')} ({t('kpi.excl_vat')}) (
                    {store === Store.FR ? Currency.EUR : Currency.USD})
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={inputs.buyingPrice || ''}
                      onChange={(e) =>
                        updateInput('buyingPrice', e.target.value)
                      }
                      type="text"
                      placeholder="ex: 24.99"
                      className="flex-1"
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog(
                          'buyingPrice',
                          inputs.buyingPrice || '',
                          (value) => value >= 0
                        )
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Eco Tax */}
              <div className="space-y-2">
                <Label>
                  {t('kpi.eee_price')} (
                  {store === Store.FR ? Currency.EUR : Currency.USD})
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.eeePrice || ''}
                    onChange={(e) => updateInput('eeePrice', e.target.value)}
                    type="text"
                    placeholder="ex: 0.05"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'eeePrice',
                        inputs.eeePrice || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* Discounted Price Choose */}
              <div className="space-y-2">
                <Label>
                  {t('kpi.discounted_price')} ({vatLabel}) (
                  {store === Store.FR ? Currency.EUR : Currency.USD})
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.discountedPrice || ''}
                    onChange={(e) =>
                      updateInput('discountedPrice', e.target.value)
                    }
                    type="text"
                    placeholder="ex: 24.99"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'discountedPrice',
                        inputs.discountedPrice || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* VAT Margin */}
              {store === Store.FR && (
                <div className="space-y-2">
                  <Label>
                    {t('kpi.vat_ms')} ({t('kpi.vat_ms_long')})
                  </Label>
                  <div className="flex flex-row justify-between">
                    <Checkbox
                      defaultChecked={hasVatMargin || false}
                      onCheckedChange={() => updateHasVatMargin(!hasVatMargin)}
                      className=""
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog(
                          'hasVatMargin' as keyof DealItem,
                          hasVatMargin ? 'true' : 'false'
                        )
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section Catégorie */}
          <div className="space-y-4">
            <h4 className="border-b border-gray-200 pb-1 text-sm font-medium text-gray-700">
              {t('kpi.type')}
            </h4>

            <div className="ml-2 space-y-4">
              {/* Catégorie de produit */}
              <div className="space-y-2">
                <Label>{t('kpi.type_long')}</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <div className="h-10 rounded-md border border-input bg-background px-2 [&_.truncate]:!max-w-full [&_span[style*='max-width']]:!max-w-full">
                      <CategorySelector
                        value={selectedCategory?.id || null}
                        onValueChange={handleCategoryChange}
                        placeholder={t('kpi.type_placeholder')}
                      />
                    </div>
                  </div>
                  <Button
                    onClick={() =>
                      selectedCategory &&
                      openConfirmDialog(
                        'productCategoryId' as keyof DealItem,
                        selectedCategory.id
                      )
                    }
                    size="sm"
                    disabled={!selectedCategory}
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Section Logistique */}
          <div className="space-y-4">
            <h4 className="border-b border-gray-200 pb-1 text-sm font-medium text-gray-700">
              {t('components.bulk_edit_sidebar.logistics')}
            </h4>

            <div className="ml-2 space-y-4">
              {/* Frais de port */}
              <div className="space-y-2">
                <Label>
                  {t('kpi.shipping_price')} ({vatLabel}) (
                  {store === Store.FR ? Currency.EUR : Currency.USD})
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.shippingPrice || ''}
                    onChange={(e) =>
                      updateInput('shippingPrice', e.target.value)
                    }
                    type="text"
                    placeholder="ex: 2.90"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'shippingPrice',
                        inputs.shippingPrice || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>
              {/* Pays de fabrication */}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">
                  {t('kpi.made_in')}
                </Label>
                <div className="flex gap-2">
                  <Popover
                    open={countryPopoverOpen}
                    onOpenChange={setCountryPopoverOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        data-user-guide="Made in"
                        variant="outline"
                        className={cn(
                          'flex-1 justify-between',
                          !inputs.madeIn && 'text-muted-foreground'
                        )}
                        type="button"
                      >
                        {inputs.madeIn
                          ? country[inputs.madeIn] || inputs.madeIn
                          : t('kpi.made_in_select')}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="z-sidebar-dropdown w-[200px] p-0">
                      <Command>
                        <CommandInput
                          placeholder={t('kpi.made_in_research_placeholder')}
                        />
                        <CountryList
                          selectedCode={inputs.madeIn}
                          countryNames={country}
                          onSelect={(code) => {
                            updateInput('madeIn', code);
                            setCountryPopoverOpen(false);
                          }}
                        />
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button
                    onClick={() =>
                      openConfirmDialog('madeIn', inputs.madeIn || '')
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* Stock */}
              <div className="space-y-2">
                <Label>{t('kpi.stock')}</Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.stockQuantity || ''}
                    onChange={(e) =>
                      updateInput('stockQuantity', e.target.value)
                    }
                    type="text"
                    placeholder="ex: 100"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'stockQuantity',
                        inputs.stockQuantity || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Section Développeur (developer-only) */}
          {user?.isDeveloper && (
            <div className="space-y-4">
              <h4 className="border-b border-gray-200 pb-1 text-sm font-medium text-gray-700">
                {t('components.bulk_edit_sidebar.developer')}
              </h4>

              <div className="ml-2 space-y-4">
                {/* GTIN */}
                <div className="space-y-2">
                  <Label>{t('kpi.gtin')}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={inputs.gtin || ''}
                      onChange={(e) => updateInput('gtin', e.target.value)}
                      type="text"
                      placeholder="ex: 1234567890123"
                      className="flex-1"
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog('gtin', inputs.gtin || '')
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Sidebar>

      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          !open && setConfirmDialog({ open: false, field: null, value: '' })
        }
      >
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {hasFieldLabel &&
                `${t('components.bulk_edit_sidebar.confirm_modify_base')} ${t(confirmDialogFieldLabels[confirmDialog.field!])} ?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {hasFieldLabel &&
                (confirmDialog.value === '' ? (
                  <span className="mt-2 flex items-start gap-2 rounded-md bg-orange-50 p-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
                    <span>
                      {t(
                        'components.bulk_edit_sidebar.confirm_edit_null_value',
                        {
                          field: t(
                            confirmDialogFieldLabels[confirmDialog.field!]
                          ),
                          count: selectedCount,
                        }
                      )}
                    </span>
                  </span>
                ) : (
                  t('components.bulk_edit_sidebar.confirm_dialog_description', {
                    count: selectedCount,
                  })
                ))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('general.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedEdit}
              data-user-guide="Confirm Bulk"
            >
              {t('general.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showShippingPolicyModal && (
        <ShippingPolicyDialog
          open
          onOpenChange={setShowShippingPolicyModal}
          dealId={dealId}
          onConfirm={closeAndResetSelection}
          mode={ShippingMode.EditBulk}
        />
      )}
    </>
  );
};

export default BulkEditSidebar;
